import { Navigate, Outlet } from 'react-router-dom';
import { AppBar, Avatar, Box, Button, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import { useAuth } from '../../context/authContext';
import { Logo } from '../../components/Logo';

/** Student Portal (Phase 67) — a deliberately separate, minimal layout
 * (no staff sidebar/nav) from the staff AppLayout. Guards the converse
 * direction of `ProtectedRoute` (auth-only): a STAFF user who somehow
 * navigates to `/portal` is redirected away, since this whole route tree
 * has no staff module:action permission gate at all (the Student role
 * holds none — see permissions.ts).
 *
 * Phase 103: IMC branding — the official logo (src/assets/imc-logo.png, the
 * same asset as the staff app's Logo) in a white header with the brand
 * purple accent, and a layout that works on a phone. */
export const PortalLayout = () => {
  const { user, logout } = useAuth();

  if (user && user.role.name !== 'Student') {
    return <Navigate to="/" replace />;
  }

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '3px solid', borderColor: 'primary.main', bgcolor: 'background.paper' }}
      >
        <Toolbar sx={{ gap: { xs: 1, sm: 2 }, minHeight: { xs: 60, sm: 68 } }}>
          <Logo height={40} sx={{ height: { xs: 30, sm: 40 } }} />
          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              borderLeft: '1px solid',
              borderColor: 'divider',
              pl: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} color="primary" lineHeight={1.2}>
              Student Portal
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }} aria-hidden>
              {initials || '?'}
            </Avatar>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 220 }}
              noWrap
            >
              {user?.firstName} {user?.lastName}
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={() => logout()}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Log out
          </Button>
          <Tooltip title="Log out">
            <IconButton
              aria-label="Log out"
              color="primary"
              onClick={() => logout()}
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 }, maxWidth: 1080, width: '100%', mx: 'auto', flexGrow: 1 }}
      >
        <Outlet />
      </Box>
      <Box component="footer" sx={{ py: 2, px: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          IMC Education · Questions about your documents or payments? Contact your counselor.
        </Typography>
      </Box>
    </Box>
  );
};
